import { useCallback, useMemo, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { parseNotionZip, orderForImport } from '../features/import/notionZip';
import { runImport } from '../features/import/runImport';
import { listNotionPages, fetchNotionTree, runNotionImport } from '../features/import/notionDirect';
import '../features/import/import-page.css';

const STAGE = { IDLE: 'idle', PARSING: 'parsing', PREVIEW: 'preview', RUNNING: 'running', DONE: 'done' };

export default function ImportPage() {
  const { user, membership } = useAuth();
  const orgId = membership?.org_id ?? null;

  const [mode, setMode] = useState('notion'); // 'notion' | 'zip'
  const [source, setSource] = useState('notion'); // 실제 가져오기 소스 (preview/start 분기)
  const [stage, setStage] = useState(STAGE.IDLE);
  const [fileName, setFileName] = useState('');
  const [nodes, setNodes] = useState([]);
  const [parseError, setParseError] = useState(null);
  // zip 첨부(비 .md 파일) 엔트리 — runImport 가 업로드 + 본문 링크 재작성에 사용
  const zipAssetsRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, title: '' });
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  // 노션 직접 가져오기 상태
  const [notionPages, setNotionPages] = useState(null); // null=미로드, []=빈목록
  const [notionLoading, setNotionLoading] = useState(false);
  const [notionErr, setNotionErr] = useState('');
  const [linkInput, setLinkInput] = useState('');

  const orderedPreview = useMemo(() => {
    if (!nodes.length) return [];
    if (source === 'notion') {
      const byId = new Map(nodes.map((n) => [n.id, n]));
      const depthOf = (n) => {
        let d = 0; let cur = n;
        while (cur && cur.parentId && byId.has(cur.parentId)) { d += 1; cur = byId.get(cur.parentId); }
        return d;
      };
      return nodes.map((n) => ({ id: n.id, title: n.title, depth: depthOf(n), kind: n.kind }));
    }
    const ordered = orderForImport(nodes.map((n) => ({ ...n })));
    const depthOf = (node) => {
      let d = 0; let cur = node.parentNode;
      while (cur) { d += 1; cur = cur.parentNode; }
      return d;
    };
    return ordered.map((n) => ({ id: n.id, title: n.title, depth: depthOf(n) }));
  }, [nodes, source]);

  /* ---------------- zip 경로 ---------------- */
  const handleFile = useCallback(async (file) => {
    if (!file) return;
    if (!/\.zip$/i.test(file.name)) { setParseError('노션에서 내보낸 .zip 파일을 선택하세요.'); return; }
    setParseError(null);
    setFileName(file.name);
    setSource('zip');
    setStage(STAGE.PARSING);
    try {
      const { nodes: parsed, assets } = await parseNotionZip(file);
      if (!parsed.length) {
        setParseError('zip 안에서 가져올 페이지(.md)를 찾지 못했습니다. 노션 Markdown & CSV 내보내기인지 확인하세요.');
        setStage(STAGE.IDLE);
        return;
      }
      zipAssetsRef.current = assets || null;
      setNodes(parsed);
      setStage(STAGE.PREVIEW);
    } catch (e) {
      setParseError(`zip 파싱 실패: ${e?.message || e}`);
      setStage(STAGE.IDLE);
    }
  }, []);

  const onInputChange = (e) => { const file = e.target.files && e.target.files[0]; handleFile(file); };
  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const file = e.dataTransfer.files && e.dataTransfer.files[0];
    handleFile(file);
  };

  /* ---------------- 노션 직접 경로 ---------------- */
  const loadNotionPages = useCallback(async () => {
    setNotionErr(''); setNotionLoading(true);
    try {
      const pages = await listNotionPages();
      setNotionPages(pages);
      if (!pages.length) setNotionErr('integration 에 공유된 페이지가 없습니다. 노션에서 가져올 페이지를 통합에 연결(공유)했는지 확인하세요.');
    } catch (e) {
      setNotionErr(e?.message || '노션 연결에 실패했습니다.');
    } finally {
      setNotionLoading(false);
    }
  }, []);

  const pickNotionPage = useCallback(async (pageId) => {
    setNotionErr(''); setSource('notion'); setFileName(''); setStage(STAGE.PARSING);
    try {
      const { nodes: fetched, truncated } = await fetchNotionTree(pageId);
      if (!fetched || !fetched.length) { setNotionErr('가져올 페이지를 찾지 못했습니다.'); setStage(STAGE.IDLE); return; }
      setNodes(fetched);
      if (truncated) setNotionErr(`페이지가 매우 많아 처음 ${fetched.length}개만 불러왔습니다.`);
      setStage(STAGE.PREVIEW);
    } catch (e) {
      setNotionErr(e?.message || '노션 페이지를 가져오지 못했습니다.');
      setStage(STAGE.IDLE);
    }
  }, []);

  const importFromLink = useCallback(async () => {
    const m = String(linkInput).match(/[0-9a-fA-F]{32}/);
    if (!m) { setNotionErr('노션 페이지 링크 또는 32자리 ID를 입력하세요.'); return; }
    await pickNotionPage(m[0]);
  }, [linkInput, pickNotionPage]);

  /* ---------------- 공통 ---------------- */
  const reset = () => {
    setStage(STAGE.IDLE); setFileName(''); setNodes([]); setParseError(null);
    zipAssetsRef.current = null;
    setProgress({ done: 0, total: 0, title: '' }); setResult(null);
    setNotionErr(''); setLinkInput('');
    if (inputRef.current) inputRef.current.value = '';
  };

  const start = async () => {
    if (!orgId || !user?.id) return;
    setStage(STAGE.RUNNING);
    setProgress({ done: 0, total: nodes.length, title: '' });
    const runner = source === 'notion' ? runNotionImport : runImport;
    const res = await runner(
      nodes,
      // assets: zip 경로에서만 사용(첨부 업로드 + 링크 재작성) — 노션 직접 경로는 무시
      { orgId, userId: user.id, assets: source === 'zip' ? zipAssetsRef.current : null },
      (p) => setProgress({ done: p.done, total: p.total, title: p.title }),
    );
    setResult(res);
    setStage(STAGE.DONE);
    if (res.success > 0) window.dispatchEvent(new Event('rt:pages'));
  };

  if (!membership) {
    return (
      <div className="imp-root"><div className="imp-wrap"><div className="empty">
        <div className="big">🧩</div>
        <div style={{ fontSize: 16, color: 'var(--text)' }}>워크스페이스 멤버가 아닙니다</div>
        <div className="muted">{user?.email} · 관리자에게 초대를 요청하세요.</div>
      </div></div></div>
    );
  }

  const pct = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <div className="imp-root">
      <div className="imp-wrap">
        <div className="imp-head">
          <span className="ico">📥</span>
          <h1>노션에서 가져오기</h1>
        </div>

        {stage === STAGE.IDLE && (
          <div className="imp-tabs">
            <button type="button" className={`imp-tab${mode === 'notion' ? ' on' : ''}`} onClick={() => setMode('notion')}>노션에서 직접</button>
            <button type="button" className={`imp-tab${mode === 'zip' ? ' on' : ''}`} onClick={() => setMode('zip')}>파일(.zip)</button>
          </div>
        )}

        {/* 노션에서 직접 */}
        {stage === STAGE.IDLE && mode === 'notion' && (
          <>
            <p className="imp-sub">
              노션 계정을 회사 통합(integration)에 연결해 두면, <b>export 없이 바로</b> 페이지·하위 페이지·이미지·파일까지
              이 워크스페이스로 가져옵니다.
            </p>
            {notionPages === null ? (
              <div className="imp-drop" onClick={loadNotionPages} style={{ cursor: 'pointer' }}>
                <div className="big">🔗</div>
                <div className="t1">{notionLoading ? '노션 연결 중…' : '노션 페이지 불러오기'}</div>
                <div className="t2">통합에 공유된 페이지 목록을 가져옵니다</div>
              </div>
            ) : (
              <div className="imp-card">
                <div className="row">
                  <span className="fname">🔗 노션 페이지 {notionPages.length}개</span>
                  <button type="button" className="imp-btn ghost" onClick={loadNotionPages}>새로고침</button>
                </div>
                <div className="imp-list">
                  {notionPages.map((p) => (
                    <button type="button" className="imp-li imp-li-btn" key={p.id} onClick={() => pickNotionPage(p.id)}>
                      <span>📄 {p.title}</span>
                      <span className="imp-go">가져오기 →</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="imp-link-row">
              <input
                className="imp-link-input"
                placeholder="또는 노션 페이지 링크 붙여넣기"
                value={linkInput}
                onChange={(e) => setLinkInput(e.target.value)}
              />
              <button type="button" className="imp-btn" onClick={importFromLink}>불러오기</button>
            </div>
            {notionErr && <div className="imp-stat"><span className="imp-pill err">{notionErr}</span></div>}
          </>
        )}

        {/* 파일(.zip) */}
        {(stage === STAGE.IDLE || stage === STAGE.PARSING) && mode === 'zip' && (
          <>
            <p className="imp-sub">
              노션에서 <b>Export → Markdown &amp; CSV</b>로 내려받은 .zip 파일을 올리면 페이지 계층 그대로 가져옵니다.
            </p>
            {/* eslint-disable-next-line */}
            <div
              className={`imp-drop${dragOver ? ' is-over' : ''}`}
              onClick={() => inputRef.current && inputRef.current.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
            >
              <div className="big">🗂️</div>
              <div className="t1">{stage === STAGE.PARSING ? 'zip 분석 중…' : 'zip 파일을 끌어다 놓거나 클릭해 선택'}</div>
              <div className="t2">노션 Markdown &amp; CSV 내보내기 (.zip)</div>
              <input ref={inputRef} type="file" accept=".zip,application/zip" onChange={onInputChange} />
            </div>
            {parseError && <div className="imp-stat"><span className="imp-pill err">{parseError}</span></div>}
          </>
        )}

        {/* 노션 트리 로딩 */}
        {stage === STAGE.PARSING && mode === 'notion' && (
          <div className="imp-card imp-prog">
            <div className="row"><span className="fname">노션에서 페이지를 가져오는 중…</span></div>
            <div className="imp-bar indet" style={{ marginTop: 14 }}><span /></div>
            <div className="label">이미지·파일도 함께 옮기느라 시간이 걸릴 수 있어요.</div>
          </div>
        )}

        {stage === STAGE.PREVIEW && (
          <div className="imp-card">
            <div className="row">
              <span className="fname">{source === 'notion' ? '🔗 노션' : `📦 ${fileName}`}</span>
              <button type="button" className="imp-btn ghost" onClick={reset}>{source === 'notion' ? '다른 페이지' : '다른 파일'}</button>
            </div>
            <p className="imp-count">가져올 페이지 <b>{nodes.length}개</b>를 찾았습니다.</p>
            <div className="imp-list">
              {orderedPreview.slice(0, 200).map((n) => (
                <div className="imp-li" key={n.id}>
                  <span className="depth">{'　'.repeat(n.depth)}{n.depth > 0 ? '└ ' : ''}</span>
                  <span>{n.kind === 'database' ? '🗂️' : '📄'} {n.title}</span>
                </div>
              ))}
              {orderedPreview.length > 200 && (
                <div className="imp-li"><span className="depth">… 외 {orderedPreview.length - 200}개</span></div>
              )}
            </div>
            {notionErr && <div className="imp-stat"><span className="imp-pill warn">{notionErr}</span></div>}
            <div className="imp-actions">
              <button type="button" className="imp-btn" onClick={start}>{nodes.length}개 페이지 가져오기</button>
              <button type="button" className="imp-btn ghost" onClick={reset}>취소</button>
            </div>
          </div>
        )}

        {stage === STAGE.RUNNING && (
          <div className="imp-card imp-prog">
            <div className="row"><span className="fname">가져오는 중…</span></div>
            <div className="imp-bar" style={{ marginTop: 14 }}><span style={{ width: `${pct}%` }} /></div>
            <div className="label">
              <span className="now">{progress.done}</span> / {progress.total} · {pct}%
              {progress.title ? <> — {progress.title}</> : null}
            </div>
          </div>
        )}

        {stage === STAGE.DONE && result && (
          <div className="imp-card imp-result">
            <div className="row">
              <span className="fname">✅ 가져오기 완료</span>
              <button type="button" className="imp-btn ghost" onClick={reset}>새로 가져오기</button>
            </div>
            <div className="imp-stat">
              <span className="imp-pill ok">성공 {result.success}개</span>
              {result.failed > 0 && <span className="imp-pill err">실패 {result.failed}개</span>}
              {result.contentFailed > 0 && <span className="imp-pill warn">본문 변환 보류 {result.contentFailed}개</span>}
            </div>
            {result.errors && result.errors.length > 0 && (
              <div className="imp-errlist">
                {result.errors.slice(0, 30).map((er, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div className="e" key={i}>· {er.stage ? `[${er.stage}] ` : ''}{er.title} — {er.message}</div>
                ))}
                {result.errors.length > 30 && <div className="e">… 외 {result.errors.length - 30}건</div>}
              </div>
            )}
            <p className="imp-note">왼쪽 사이드바에서 가져온 페이지를 확인하세요.</p>
          </div>
        )}
      </div>
    </div>
  );
}
